import React, { useMemo } from 'react'
import { useGetOverallStats } from '../../../../../api/dealership/useGetOverallStats'
import {
  ActionDelete,
  ActionDownload,
  ActionEdit,
  AsyncSourceCard,
  AsyncStatsCard,
  Button,
  SourceSuggestion,
  Table,
} from '@demandlocal/components'
import { Link, useNavigate } from 'react-router-dom'
import InfoMessage from '../../../../../components/InfoMessage'
import { useDealership } from '../../../../../store/zustand/dealershipStore'
import { useTranslation } from 'react-i18next'
import { useGetAudiences } from '../../../../../api/dealership/customers/audiences/useGetAudiences'
import EmptyAudiencesList from '../../../../../components/Audiences/EmptyAudiencesList'
import { ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { Audience } from '../../../../../lib/types/Audience'
import { formatNumber } from '../../../../../lib/utils/formatNumber'
import dayjs from 'dayjs'
import { useModal } from '../../../../../components/useModal'
import { currentUserSelector } from '../../../../../store/selectors/userSelector'
import { useSelector } from 'react-redux'
import { ReactComponent as GoogleIcon } from './../../../../../images/icons/audience_google.svg'
import { ReactComponent as BingIcon } from './../../../../../images/icons/audience_bing.svg'
import { ReactComponent as BasisLogo } from '../../../../../images/icons/audience_basis.svg'
import { ReactComponent as MailchimpIcon } from './../../../../../images/icons/audience_mailchimp.svg'
import { ReactComponent as FacebookIcon } from './../../../../../images/icons/audience_facebook.svg'
import { ReactComponent as TradeDeskIcon } from './../../../../../images/icons/audience_tradedesk.svg'
import { ReactComponent as BrevoIcon } from './../../../../../images/icons/audience_brevo.svg'
import { ReactComponent as AmazonIcon } from './../../../../../images/icons/audience_amazon.svg'
import DeleteAudienceModal from '../../../../../forms/DeleteAudience/DeleteAudienceModal'
import EditAudience from '../../../../../forms/EditAudience/EditAudience'
import classNames from 'classnames'
import toast from 'react-hot-toast'
import { useDownloadAudienceByAudienceId } from '../../../../../api/dealership/customers/audiences/useDownloadAudienceByAudienceId'
import { ReactComponent as ActionDeploy } from './../../../../../images/icons/action_deploy.svg'
import LoadingAnimation from '../../../../../components/LoadingAnimation'
import { useGetDealerProfileSources } from '../../../../../api/dealership/profile/useGetDealerProfileSources'

const columnHelper = createColumnHelper<
  Audience & { mappedAudiences: Record<string, number> }
>()

const loadingSource = {
  customer_source: '',
  update_date: '',
  total_customers: 0,
  id: 0,
}

export default function SelfServiceProfile(): JSX.Element {
  const dealership = useDealership()
  const { t } = useTranslation('routes')
  const navigate = useNavigate()
  const { handleModal } = useModal()
  const currentUser = useSelector(currentUserSelector)

  const { data: stats, isLoading: isLoadingStats } = useGetOverallStats()
  const { data: audiences, isLoading: isLoadingAudiences } = useGetAudiences({})

  const { mutate: downloadAudience, isLoading: isDownloading } =
    useDownloadAudienceByAudienceId()

  const {
    data: sourcesList = { sources: [loadingSource, loadingSource, loadingSource] },
    isLoading: isLoadingSources,
  } = useGetDealerProfileSources()

  const columns = useMemo(
    () =>
      [
        columnHelper.accessor('audience_name', {
          header: 'Name',
          cell: (info) => (
            <Link
              to={`${info.row.original._id}`}
              className={'text-primary-persian-blue font-bold'}>
              {info.row.original.audience_name}
            </Link>
          ),
          enableSorting: false,
        }),
        columnHelper.accessor('description', {
          header: 'Description',
          cell: (info) => info.getValue() || '-',
          enableSorting: false,
        }),
        columnHelper.accessor('audience_size', {
          header: 'Audience size',
          cell: (info) => {
            return formatNumber(info.getValue() || 0)
          },
          enableSorting: false,
        }),
        columnHelper.accessor('last_run', {
          header: 'Last update',
          cell: (info) =>
            info.getValue() ? dayjs(info.getValue()).format('MMM DD, YYYY') : '-',
          enableSorting: false,
        }),
        columnHelper.display({
          id: 'linked_to',
          header: () => 'Linked to',
          cell: ({ row }) => (
            <div className={'flex flex-row gap-4'}>
              <div className={'relative'}>
                <GoogleIcon />
                {row.original.mappedAudiences?.google > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.google}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <BingIcon />
                {row.original.mappedAudiences?.bing > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.bing}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <MailchimpIcon />
                {row.original.mappedAudiences?.mailchimp > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.mailchimp}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <FacebookIcon />
                {row.original.mappedAudiences?.facebook > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.facebook}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <TradeDeskIcon />
                {row.original.mappedAudiences?.tradedesk > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.tradedesk}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <BasisLogo className={'w-6 h-6'} />
                {row.original.mappedAudiences?.basis > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.basis}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <AmazonIcon className={'w-6 h-6'} />
                {row.original.mappedAudiences?.amazon > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.amazon}
                  </span>
                )}
              </div>
              <div className={'relative'}>
                <BrevoIcon className={'w-6 h-6'} />
                {row.original.mappedAudiences?.brevo > 0 && (
                  <span
                    className={
                      'absolute flex justify-center font-Spartan font-bold -bottom-1 -right-2 bg-white border-2 rounded-full w-4 h-4 text-[9px] border-primary-persian-blue'
                    }>
                    {row.original.mappedAudiences?.brevo}
                  </span>
                )}
              </div>
            </div>
          ),
        }),
        currentUser?.role_id === 1
          ? columnHelper.display({
              id: 'created_by',
              enableHiding: true,
              header: 'Created By',
              cell: (info) => {
                const name = `${info.row.original.created_by?.first_name ?? ''} ${
                  info.row.original.created_by?.last_name ?? ''
                }`
                return name.trim()?.length > 0 ? name : '-'
              },
              enableSorting: false,
            })
          : null,
        columnHelper.display({
          id: 'actions',
          header: () => 'Actions',
          cell: ({ row }) => {
            const isNewAudienceType = dayjs(row.original.createdAt).isAfter(
              dayjs().year(2023).month(0).day(16),
            )
            return (
              <div className={'flex flex-row gap-2 items-center'}>
                <ActionDelete
                  // @ts-ignore
                  className={'cursor-pointer'}
                  onClick={() =>
                    handleModal(<DeleteAudienceModal audience={row.original} />)
                  }
                />
                <ActionEdit
                  // @ts-ignore
                  stroke={isNewAudienceType ? '#0932C2' : '#ccc'}
                  className={'cursor-pointer'}
                  onClick={(e) => {
                    e.preventDefault()
                    if (isNewAudienceType)
                      handleModal(<EditAudience audience_id={row.original._id} />)
                  }}
                />
                {dealership?.enable_audience_download && (
                  <ActionDownload
                    // @ts-ignore
                    fill={isLoading ? '#ccc' : '#0932C2'}
                    className={classNames('', {
                      'cursor-not-allowed': isDownloading,
                      'cursor-pointer': !isDownloading,
                    })}
                    onClick={() => {
                      const toastId = toast.loading('Downloading audience...')
                      if (isDownloading) {
                        toast.error('Already downloading an audience, please wait.', {
                          duration: 5000,
                        })
                      } else {
                        downloadAudience(
                          { audience_id: row.original._id },
                          {
                            async onSuccess(data) {
                              const blob = await data.blob()
                              const url = window.URL.createObjectURL(blob)
                              const a = document.createElement('a')
                              a.href = url
                              a.download = `${row.original.audience_name}.csv`
                              document.body.appendChild(a) // we need to append the element to the dom -> otherwise it will not work in firefox
                              a.click()
                              a.remove()
                              toast.success('Audience downloaded', {
                                duration: 5000,
                                id: toastId,
                              })
                            },
                          },
                        )
                      }
                    }}
                  />
                )}
                <Button
                  key={row.original._id}
                  animate={false}
                  variant="secondary"
                  onClick={(e) => {
                    e.preventDefault()
                    navigate(
                      t('dealership.audiences.deploy', {
                        dealership,
                        audience: { _id: row.original._id },
                      }),
                    )
                  }}
                  rightSlot={
                    <ActionDeploy className={'cursor-pointer fill-primary-xiketic'} />
                  }>
                  Push
                </Button>
              </div>
            )
          },
        }),
      ]
        .filter((col) => col !== null)
        .map((col) => col as unknown as ColumnDef<unknown, unknown>),
    [
      currentUser?.role_id,
      dealership,
      isDownloading,
      handleModal,
      downloadAudience,
      navigate,
      t,
    ],
  )

  return (
    <div className="flex flex-col gap-10 w-full font-Spartan px-10">
      <div className="flex flex-row gap-3">
        <AsyncStatsCard
          title={undefined}
          border
          color="bg-white"
          isLoading={isLoadingStats}
          content={
            <div className="flex flex-row justify-between items-center">
              <div className="flex flex-col">
                <span className="text-xs">Total Customers</span>
                <span className="text-md font-bold tedxt-primary-persian-blue">
                  {stats?.totalCustomers}
                </span>
              </div>
              <Link
                className="text-primary-persian-blue text-sm font-bold"
                to={t('dealership.customers.sources.list', { dealership })}>
                See All Customers
              </Link>
            </div>
          }
        />
        {isLoadingStats || (stats?.totalVehicles && stats.totalVehicles > 0) ? (
          <AsyncStatsCard
            title={undefined}
            border
            color="bg-white"
            isLoading={isLoadingStats}
            content={
              <div className="flex flex-row justify-between">
                <div className="flex flex-col">
                  <span className="text-xs">Total Vehicles</span>
                  <span className="text-md font-bold tedxt-primary-persian-blue">
                    {stats?.totalVehicles}
                  </span>
                </div>
                <Link
                  className="text-primary-persian-blue text-sm font-bold"
                  to={t('dealership.vehicles.list', { dealership })}>
                  See Inventory
                </Link>
              </div>
            }
          />
        ) : (
          <InfoMessage
            content={
              <div className="flex flex-col gap-3 py-2">
                <span className="text-xs">You have no inventory linked</span>
                <Link
                  className="font-bold text-primary-persian-blue"
                  to={t('dealership.vehicles.list', { dealership })}>
                  + Link Your Inventory
                </Link>
              </div>
            }
          />
        )}
      </div>

      <div className="flex flex-row justify-between items-center">
        <div className="flex flex-row gap-3 items-center">
          <span className="font-bold">Your Audiences</span>
          <Link
            className="text-primary-persian-blue font-bold text-sm"
            to={t('dealership.audiences.custom', { dealership })}>
            See all ({stats?.totalAudiences})
          </Link>
        </div>
        <div className="flex flex-row gap-3 divide-x-2">
          <div>
            <Button
              variant="secondary"
              onClick={() =>
                navigate(t('dealership.audiences.custom', { dealership }))
              }>
              Audience Builder
            </Button>
          </div>
          <div className="pl-3">
            <Button
              variant="secondary"
              onClick={() =>
                navigate(t('dealership.audiences.create', { dealership }))
              }>
              + Add New
            </Button>
          </div>
        </div>
      </div>
      <div>
        {isLoadingAudiences && (
          <div className="flex justify-center items-center w-full p-2">
            <LoadingAnimation />
          </div>
        )}

        {!isLoadingAudiences && (audiences?.pagination?.totalDocs ?? 0) > 0 && (
          <Table
            limit={5}
            hidePagination
            columns={columns}
            filters={[]}
            fetchFn={useGetAudiences}
          />
        )}
        {!isLoadingAudiences && audiences?.pagination?.totalDocs === 0 && (
          <EmptyAudiencesList />
        )}
      </div>
      <InfoMessage
        content={
          <div className="flex flex-row justify-between items-center p-3 w-full">
            <div className="flex flex-col gap-3 pr-5">
              <span className="text-lg font-bold">
                Save Time, Do more, with Our Full-Service Solution!
              </span>
              <span>
                Need some help? Unlock expert guidance, tailored strategies, and proven
                results to grow your audience, market your cars, and create highly
                effective targeted ads. Save time and boost productivity with our
                personalized support!
              </span>
            </div>
            <Button
              variant="rainbow"
              onClick={(e) => {
                e.preventDefault()
                open(
                  `mailto:${
                    dealership?.sales_manager ??
                    dealership?.account_manager ??
                    'sales@link1data.com'
                  }?subject=Link1Data Full Service Setup&body=We are interested in setting up some full service campaigns!`,
                  '_blank',
                )
              }}>
              Let's Get Started
            </Button>
          </div>
        }
      />
      <div className="flex flex-col gap-3 pt-5">
        <div className="flex flex-row justify-between items center">
          <span className="font-bold">Sources</span>
          <Button
            animate={false}
            variant="secondary"
            onClick={() => navigate('sources/new')}>
            Add more sources
          </Button>
        </div>
        <div className="grid grid-cols-3 w-full gap-5">
          {sourcesList.sources?.slice(0, 3).map((source, idx) => (
            <div key={idx}>
              <AsyncSourceCard
                title={source.customer_source}
                last_updated={source.updated_date}
                total_customers={source.total_customers}
                id={source.id}
                isLoading={isLoadingSources}
                to={t('dealership.customers.sources.report', { dealership, source })}
              />
            </div>
          ))}
          {!isLoadingSources &&
            Array.from({ length: 3 - sourcesList?.sources?.length }).map((_, idx) => (
              <SourceSuggestion
                key={`suggestions${idx}`}
                index={idx}
                to={t('dealership.customers.sources.create', { dealership })}
              />
            ))}
        </div>
      </div>
    </div>
  )
}
